import { StackScreenProps } from "@react-navigation/stack";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import {
  ActiveInvoiceArrivalItemContext,
  useInvoiceArrivalContextProvider,
} from "../../utils/active-invoice-arrival";
import {
  EditInvoiceArrivalRootParamList,
  EditInvoiceLineItemRootScreenParamList,
} from "../../../../types";
import React from "react";
import { EditInvoiceLineItemScreen } from "../EditInvoiceLineItemScreen";
import { InvoiceArrivalItemForm } from "../../components";
import { InvoiceArrivalItemFormValues } from "../../components/InvoiceArrivalItemForm/types";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { useFormContext } from "react-hook-form";
import { ProductFormActionsContext } from "../../../products/components/ProductForm/context";
import { CreateProductRootScreen } from "../../../products/screens/CreateProductRootScreen";
import { itemToFormInput } from "../../utils/utils";
import { EditProductRootScreen } from "../../../products/screens";

export type EditInvoiceLineItemRootScreenProps = StackScreenProps<
  EditInvoiceArrivalRootParamList,
  "EditInvoiceLineItemRootScreen"
>;

const EditInvoiceLineItemRoot = ({
  navigation,
}: EditInvoiceLineItemRootScreenProps) => {
  const { setValue } = useFormContext<InvoiceArrivalItemFormValues>();

  const Drawer =
    createStackHeaderNavigator<EditInvoiceLineItemRootScreenParamList>();

  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  return (
    <ProductFormActionsContext.Provider
      value={{
        onAfterAddProduct: async (product, _) => {
          await fetchAndSyncEntity("products");

          setValue("productId", product.id, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });

          const { webMarkup, tradeMarkup, retailMarkup } = itemToFormInput({
            product,
          });
          setValue("webMarkup", webMarkup);
          setValue("tradeMarkup", tradeMarkup);
          setValue("retailMarkup", retailMarkup);
        },
      }}
    >
      <Drawer.Navigator
        screenOptions={{
          animationEnabled: true,
          headerLeft: () => <ModalCloseButton type="Back" />,
          title: "",
        }}
      >
        <Drawer.Group>
          <Drawer.Screen
            name="EditInvoiceLineItemScreen"
            component={EditInvoiceLineItemScreen}
          />
        </Drawer.Group>
        <Drawer.Group
          screenOptions={{
            headerShown: false,
          }}
        >
          <Drawer.Screen
            name="CreateProductRootScreen"
            component={CreateProductRootScreen}
          />
        </Drawer.Group>

        <Drawer.Group
          screenOptions={{
            headerShown: false,
            presentation: "modal",
          }}
        >
          <Drawer.Screen
            name="EditProductRootScreen"
            component={EditProductRootScreen}
          />
        </Drawer.Group>
      </Drawer.Navigator>
    </ProductFormActionsContext.Provider>
  );
};

export const EditInvoiceLineItemRootScreen = (
  props: EditInvoiceLineItemRootScreenProps
) => {
  const { invoiceArrival } = useInvoiceArrivalContextProvider();
  const invoiceArrivalItemId = props.route.params.lineItemId;

  const item = React.useMemo(() => {
    return invoiceArrival.items.find(
      (item) => item.id === invoiceArrivalItemId
    );
  }, [invoiceArrival, invoiceArrivalItemId]);

  if (!item) {
    /* This should never happen */
    return null;
  }

  const defaultValues = itemToFormInput(item);

  return (
    <ActiveInvoiceArrivalItemContext.Provider value={{ item }}>
      <InvoiceArrivalItemForm.Provider defaultFormValues={defaultValues}>
        <EditInvoiceLineItemRoot {...props} />
      </InvoiceArrivalItemForm.Provider>
    </ActiveInvoiceArrivalItemContext.Provider>
  );
};
